import React, { useMemo, useState, useCallback } from 'react'
import { HoursTableColumns } from '../../columnTemplates'
import { useReactTable, getCoreRowModel } from '@tanstack/react-table'
import './index.css'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import ColumnsSelector from '../ColumnsSelection/ColumnSelector'

import { totalHours, totalDays, hoursTardy } from '../../functions/computations'
import useZoom from '../useZoom'

const SevenStarHoursTable = ({ data = [], store = '' }) => {
    const { zoom, handleZoomIn, handleZoomOut, resetZoom } = useZoom();


    // Columns based on provided payroll data structure
    const HoursTableColumns = useMemo(() => [
    {
        header: 'EMP No',
        accessorFn: (row) => {
            // Note: Ensure 'data' is available in the scope or passed as a dependency
            let index = data.findIndex(rowOnData => rowOnData.id === row.id);
            return index !== -1 ? index + 1 : "N/A";
        },
        accessorKey: 'id',
        id: 'index'
    },
    { accessorKey: 'employee_name', header: 'Employee Name' },
    { accessorKey: 'hours_work', header: 'Regular Hours' },
    { accessorKey: 'overtime', header: 'Overtime Hours' },
    {
        header: 'Late (minutes)',
        accessorFn: (row) => {
            let lates = Number(row?.hours_tardy || 0).toFixed(2);
            return Number(lates);
        },
        id: 'late_minutes',
        accessorKey: 'late_minutes'
    },
    { accessorKey: 'night_diff', header: 'Night diff (minutes)' },
    {
        header: 'Special Holiday (hrs)',
        accessorFn: (row) => Number(row?.special_holiday || 0),
        accessorKey: 'special_holiday',
        id: 'special_holiday'
    },
    {
        header: 'Special Holiday OT (hrs)',
        accessorFn: (row) => Number(row?.special_holiday_overtime || 0),
        accessorKey: 'special_holiday_overtime',
        id: 'special_holiday_overtime'
    },
    {
        header: 'Legal Holiday (hrs)',
        accessorFn: (row) => Number(row?.legal_holiday || 0),
        accessorKey: 'legal_holiday',
        id: 'legal_holiday'
    },
    {
        header: 'Legal Holiday OT (hrs)',
        accessorFn: (row) => Number(row?.legal_holiday_overtime || 0),
        accessorKey: 'legal_holiday_overtime',
        id: 'legal_holiday_overtime'
    },
    {
        header: 'Rest Day (hrs)',
        accessorFn: (row) => Number(row?.rest_day_hours || 0),
        accessorKey: 'rest_day_hours',
        id: 'rest_day_hours'
    },
    {
        header: 'Rest Day Overtime (hrs)',
        accessorFn: (row) => Number(row?.rest_day_overtime || 0),
        accessorKey: 'rest_day_overtime',
        id: 'rest_day_overtime'
    },
    {
        header: 'Rest Day Legal Holiday',
        accessorFn: (row) => Number(row?.rest_day_legal_holiday || 0),
        accessorKey: 'rest_day_legal_holiday',
        id: 'rest_day_legal_holiday'
    },
    {
        header: 'Rest Day OT Legal Holiday',
        accessorFn: (row) => Number(row?.rest_day_ot_legal_holiday || 0),
        accessorKey: 'rest_day_ot_legal_holiday',
        id: 'rest_day_ot_legal_holiday'
    },
    {
        header: 'Rest Day Special Holiday',
        accessorFn: (row) => Number(row?.rest_day_special_holiday || 0),
        accessorKey: 'rest_day_special_holiday',
        id: 'rest_day_special_holiday'
    },
    {
        header: 'Rest Day OT Special Holiday',
        accessorFn: (row) => Number(row?.rest_day_ot_special_holiday || 0),
        accessorKey: 'rest_day_ot_special_holiday',
        id: 'rest_day_ot_special_holiday'
    },
    {
        header: 'Late (hrs)',
        accessorFn: (row) => {
            // Ensure hoursTardy function is defined in your component
            let hours = typeof hoursTardy === 'function' ? hoursTardy(row) : (row.hours_tardy / 60);
            return hours < 0 ? 0 : Number(hours);
        },
        accessorKey:'late_hrs',
        id: 'late_hrs'
    },
    {
        header: 'Total Hours',
        accessorFn: (row) => {
            return (Number(row.hours_work) || 0) + 
                   (Number(row.overtime) || 0) + 
                   (Number(row.special_holiday) || 0) - 
                   (Number(row.hours_tardy) || 0);
        },
        accessorKey: 'total_hours',
        id: 'total_hours'
    },
    {
        header: 'Total Days',
        accessorFn: (row) => {
            const total = (Number(row.hours_work) || 0) + 
                          (Number(row.overtime) || 0) + 
                          (Number(row.special_holiday) || 0) - 
                          (Number(row.hours_tardy) || 0);
            return total > 0 ? Math.ceil(total / 8) : 0; // Assuming 8 hours per day
        },
        accessorKey: 'total_days',
        id: 'total_days'
    }
], [data]); // Added 'data' as a dependency since it's used in the first column

    const [selectedColumns, setSelectedColumns] = useState(HoursTableColumns)

    const table = useReactTable({
        data: data,
        columns: selectedColumns,
        getCoreRowModel: getCoreRowModel(),

    })
    // Render a simple table using useReactTable

    const rows = table.getRowModel().rows;

    // Assuming HoursTableColumns is an array of column definitions with 'accessorKey' and 'header'
    const columns = selectedColumns;

    const { sum, sumTotalHours, sumTotalDays, sumHoursTardy } = useMemo(() => {
        const sum = (key) => data.reduce((acc, row) => acc + (row[key] || 0), 0);

        const sumTotalHours = data.reduce((acc, row) => {
            let total = totalHours(row);
            return acc + total;
        }, 0)

        const sumTotalDays = data.reduce((acc, row) => {
            let days = totalDays(row);
            return acc + days;
        }, 0);

        const sumHoursTardy = data.reduce((acc, row) => {
            let tardy = hoursTardy(row);
            return Number(acc) + Number(tardy);
        }, 0);

        return { sum, sumTotalHours, sumTotalDays, sumHoursTardy };
    }, [data]);

    return (
        <>
            <Paper ></Paper>
            <Paper >
                <Stack direction='row' sx={{ p: 1, bgcolor: '#f8f9fa', borderBottom: '1px solid #e5e7eb' }} justifyContent='space-between'>
                    <Typography variant="h6" component="div" >
                        Hours Table
                    </Typography>


                    <Stack direction='row' spacing={1} >
                        <Tooltip title="Zoom In">
                            <IconButton
                                size="small"
                                sx={{
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 2,
                                    '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                                }}
                                onClick={handleZoomIn}
                            >
                                <ZoomInIcon fontSize="small" sx={{ color: '#6b7280' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Zoom out">
                            <IconButton
                                onClick={handleZoomOut}
                                size="small"
                                sx={{
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 2,
                                    '&:hover': { bgcolor: '#f9fafb', borderColor: '#12C1DC' }
                                }}
                            >
                                <ZoomOutIcon fontSize="small" sx={{ color: '#6b7280' }} />
                            </IconButton>
                        </Tooltip>

                        <Button onClick={resetZoom}>Reset Zoom</Button>
                    </Stack>


                </Stack>

                <Stack p={1} >
                    <Typography variant='subtitle2' color='text.secondary'>Select columns to be displayed here</Typography>
                    <ColumnsSelector selectedColumns={selectedColumns.map(({ accessorKey }) => accessorKey)} columnTemplates={HoursTableColumns} onColumnsChange={setSelectedColumns} />
                </Stack>


                <div className='hours-table__container'>
                    <div className="hours-table__wrapper">
                        {selectedColumns.length === 0 ? <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                            <Typography variant="body1" color="text.secondary">
                                Please select columns
                            </Typography>
                        </Box> : null}
                        <table id='hours_table' className="hours-table" style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}>
                            <thead className="hours-table__header">
                                <tr>
                                    {columns.map((col, idx) => (
                                        <th key={col.accessorKey || col.id || idx}>
                                            {col.header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="hours-table__body">
                                {rows.map((row) => (
                                    <tr key={row.id}>
                                        {columns.map((col, idx) => {
                                            let value;
                                            let textAlign = 'left'
                                            if (typeof col.accessorFn === 'function') {
                                                value = col.accessorFn(row.original);
                                            } else if (col.accessorKey) {
                                                value = row.original[col.accessorKey];
                                            } else {
                                                value = '';
                                            }

                                            value = Number(value) ? parseFloat(Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })) : value

                                            if (typeof value == 'number')
                                                textAlign = 'right'

                                            if (col.header === 'EMP No') {
                                                textAlign = 'center'
                                                value = parseFloat(Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 }))
                                            }

                                            if(col.header === 'Employee Name') {
                                                value = String(value).toUpperCase()
                                            }

                                            return (
                                                <td key={col.accessorKey || col.id || idx} style={{
                                                    textOverflow: 'ellipsis',
                                                    overflow: 'hidden',
                                                    textAlign: textAlign,
                                                    maxWidth: col.header === 'Employee Name' ? '150px' : 'auto'
                                                }}>
                                                    {value}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}

                                <tr style = {{
                                    backgroundColor: 'yellow', 
                                    fontWeight: 'bold'
                                }}>
                                    {columns.map((col, idx) => {
                                        let value = '';
                                        switch (col.header) {
                                            case 'Regular Hours':
                                                value = sum('hours_work');
                                                break;
                                            case 'Overtime Hours':
                                                value = sum('overtime');
                                                break;
                                            case 'Late (minutes)':
                                                value = parseFloat(Number(sum('hours_tardy') || 0).toFixed(2));
                                                break;
                                            case 'Special Holiday (hrs)':
                                                value = sum('special_holiday');
                                                break;
                                            case 'Actual Days':
                                                value = sum('days_work');
                                                break;
                                            case 'Total Hours':
                                                value = sumTotalHours;
                                                break;
                                            case 'Total Days':
                                                value = sumTotalDays;
                                                break;


                                            case 'Legal Holiday (hrs)': 
                                                value = sum('legal_holiday')
                                                break;

                                            case 'Night diff (minutes)':
                                                value = sum('night_diff')
                                                break;

                                            case 'Special Holiday OT (hrs)':
                                                value = sum('special_holiday_overtime')
                                                break;

                                            case 'Legal Holiday(hrs)':
                                                value = sum('legal_holiday')
                                                break;

                                            case 'Legal Holiday OT (hrs)':
                                                value = sum('legal_holiday_overtime')
                                                break;

                                            case 'Rest Day (hrs)':
                                                value = sum('rest_day_hours')
                                                break;

                                            case 'Rest Day Overtime (hrs)':
                                                value = sum('rest_day_overtime')
                                                break;

                                            case 'Rest Day Legal Holiday':
                                                value = sum('rest_day_legal_holiday')
                                                break;

                                            case 'Rest Day OT Legal Holiday':
                                                value = sum('rest_day_ot_legal_holiday')
                                                break;

                                            case 'Rest Day Special Holiday':
                                                value = sum('rest_day_special_holiday')
                                                break;

                                            case 'Rest Day OT Special Holiday':
                                                value = sum('rest_day_ot_special_holiday')
                                                break;
                                            
                                            case 'Late (hrs)':
                                                value = sumHoursTardy;
                                                break;
                                            default:
                                                value = '';
                                        }
                                        return (
                                            <td key={col.accessorKey || col.id || idx} style={{
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                textAlign: typeof value === 'number' ? 'right' : 'left',
                                                maxWidth: col.header === 'Employee Name' ? '150px' : 'auto'
                                            }}>
                                                {Number(value) ? Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : value}
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tbody>


                            {/*
                            <tfoot className="hours-table__footer">
                                <tr>
                                    {columns.map((col, idx) => {
                                        let value = '';
                                        switch (col.header) {
                                            case 'Regular Hours':
                                                value = sum('hours_work');
                                                break;
                                            case 'Overtime Hours':
                                                value = sum('overtime');
                                                break;
                                            case 'Late (minutes)':
                                                value = parseFloat(Number(sum('hours_tardy') || 0).toFixed(2));
                                                break;
                                            case 'Special Holiday (hrs)':
                                                value = sum('special_holiday');
                                                break;
                                            case 'Actual Days':
                                                value = sum('days_work');
                                                break;
                                            case 'Total Hours':
                                                value = sumTotalHours;
                                                break;
                                            case 'Total Days':
                                                value = sumTotalDays;
                                                break;

                                            case 'Night diff (minutes)':
                                                value = sum('night_diff')
                                                break;

                                            case 'Special Holiday OT (hrs)':
                                                value = sum('special_holiday_overtime')
                                                break;

                                            case 'Legal Holiday(hrs)':
                                                value = sum('legal_holiday')
                                                break;

                                            case 'Legal Holiday OT (hrs)':
                                                value = sum('legal_holiday_overtime')
                                                break;

                                            case 'Rest Day (hrs)':
                                                value = sum('rest_day_hours')
                                                break;

                                            case 'Rest Day Overtime (hrs)':
                                                value = sum('rest_day_overtime')
                                                break;

                                            case 'Rest Day Legal Holiday':
                                                value = sum('rest_day_legal_holiday')
                                                break;

                                            case 'Rest Day OT Legal Holiday':
                                                value = sum('rest_day_ot_legal_holiday')
                                                break;

                                            case 'Rest Day Special Holiday':
                                                value = sum('rest_day_special_holiday')
                                                break;

                                            case 'Rest Day OT Special Holiday':
                                                value = sum('rest_day_ot_special_holiday')
                                                break;
                                            default:
                                                value = '';
                                        }
                                        return (
                                            <td key={col.accessorKey || col.id || idx} style={{
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                textAlign: typeof value === 'number' ? 'right' : 'left',
                                                maxWidth: col.header === 'Employee Name' ? '150px' : 'auto'
                                            }}>
                                                {Number(value) ? Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : value}
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tfoot>
                            */}
                        </table>
                    </div>
                </div>
            </Paper>
        </>
    );
}

export default SevenStarHoursTable